import React, {useCallback, useState} from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {SearchInput} from '../../search/SearchInput';
import {
  adminUsersCache,
  adminUserStatsCache,
} from '../../../common/constants/queryCacheKey';
import {useAuthStore} from '../../../common/stores/useAuthStore';
import {
  AdminUser,
  AdminUserSortBy,
  createUser,
  CreateUserInput,
  deleteUser,
  fetchAdminUsers,
  fetchAdminUserStats,
  roleLabels,
  updateUserName,
} from '../../../common/services/users';
import type {SortDirection} from '../../../common/services/activities';
import {UserSettingsPanel} from './UserSettingsPanel';
import {useGuardedMutate} from '../../../common/hooks/useGuardedMutate';

const PAGE_SIZE = 20;

const emptyNewUser: CreateUserInput = {fullName: '', email: '', password: ''};

// The users table keeps at most 40 characters of a name; longer fails with a 500.
const NAME_MAX = 40;

const sortOptions: {label: string; value: AdminUserSortBy}[] = [
  {label: 'Date created', value: 'createdAt'},
  {label: 'Name', value: 'name'},
  {label: 'Email', value: 'email'},
  {label: 'Role', value: 'role'},
];

const formatCreatedAt = (value?: string | null): string | null =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

const StatCard: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
}> = ({label, value, highlight = false}) => (
  <div style={highlight ? highlightStatCardStyle : statCardStyle}>
    <div style={{fontSize: '2.4rem', fontWeight: 'bold'}}>{value}</div>
    <div style={{fontSize: '0.9rem', opacity: 0.8}}>{label}</div>
  </div>
);

export const UsersScreen: React.FC = () => {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore(state => state.session?.userId);

  const [keyword, setKeyword] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState<AdminUserSortBy>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isAdding, setIsAdding] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserInput>(emptyNewUser);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [settingsUserId, setSettingsUserId] = useState<string | null>(null);

  const handleSearch = useCallback((value: string) => {
    setKeyword(value);
    setPageNumber(1);
  }, []);

  const handleSortByChange = (value: AdminUserSortBy) => {
    setSortBy(value);
    setPageNumber(1);
  };

  const toggleSortDirection = () => {
    setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
    setPageNumber(1);
  };

  const statsQuery = useQuery({
    queryKey: [adminUserStatsCache],
    queryFn: fetchAdminUserStats,
  });

  const usersQuery = useQuery({
    queryKey: [adminUsersCache, keyword, pageNumber, sortBy, sortDirection],
    queryFn: () =>
      fetchAdminUsers(keyword, pageNumber, PAGE_SIZE, {sortBy, sortDirection}),
    placeholderData: keepPreviousData,
  });

  const refresh = () => {
    queryClient.invalidateQueries({queryKey: [adminUsersCache]});
    queryClient.invalidateQueries({queryKey: [adminUserStatsCache]});
  };

  const onMutationError = (error: Error) => setActionError(error.message);

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (_, input) => {
      setIsAdding(false);
      setNewUser(emptyNewUser);
      setActionError(null);
      setNotice(
        `${input.fullName} was added. They get an email to confirm ${input.email}.`,
      );
      refresh();
    },
    onError: onMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: ({id, fullName}: {id: string; fullName: string}) =>
      updateUserName(id, fullName),
    onSuccess: () => {
      setEditingId(null);
      setActionError(null);
      setNotice(null);
      refresh();
    },
    onError: onMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: (user: AdminUser) => deleteUser(user.id),
    onSuccess: (_, user) => {
      setActionError(null);
      setNotice(`${user.fullName || user.email} was deleted.`);
      refresh();
    },
    onError: onMutationError,
  });

  const create = useGuardedMutate(createMutation.mutate);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const fullName = newUser.fullName.trim();
    // "required" lets a name of only spaces through, and the API takes it.
    if (!fullName) {
      setActionError('Enter the user\'s name.');
      return;
    }
    setActionError(null);
    setNotice(null);
    create({
      fullName,
      email: newUser.email.trim(),
      password: newUser.password,
    });
  };

  const startEditing = (user: AdminUser) => {
    setEditingId(user.id);
    setEditingName(user.fullName);
  };

  const saveEditing = (id: string) => {
    const fullName = editingName.trim();
    if (!fullName) {
      setActionError('The name cannot be empty.');
      return;
    }
    setActionError(null);
    updateMutation.mutate({id, fullName});
  };

  const handleDelete = (user: AdminUser) => {
    if (
      window.confirm(
        `Delete ${user.fullName || 'this user'} (${user.email})? This removes the account and cannot be undone.`,
      )
    ) {
      setNotice(null);
      deleteMutation.mutate(user);
    }
  };

  const stats = statsQuery.data;
  const users = usersQuery.data?.items ?? [];
  const maxPage = usersQuery.data?.maxPage ?? 0;
  const matchingUsers = usersQuery.data?.totalItems ?? 0;

  return (
    <div style={screenStyle}>
      <div style={{maxWidth: '900px', margin: '0 auto'}}>
        <h1 style={{color: '#fff', textAlign: 'center', marginBottom: '30px'}}>
          Users
        </h1>

        <div style={statsRowStyle}>
          <StatCard
            label="New users in the last 7 days"
            value={
              statsQuery.isPending
                ? '…'
                : (stats?.newUsersLastWeek?.toString() ?? '—')
            }
            highlight
          />
          <StatCard
            label="Total users"
            value={
              statsQuery.isPending ? '…' : (stats?.totalUsers.toString() ?? '—')
            }
          />
        </div>

        <div style={toolbarStyle}>
          <div style={{flex: '1 1 260px', maxWidth: '600px'}}>
            <SearchInput
              placeholder="Search by name or email..."
              onSearch={handleSearch}
            />
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <label htmlFor="users-sort" style={{color: '#999'}}>
              Sort by
            </label>
            <select
              id="users-sort"
              value={sortBy}
              onChange={e =>
                handleSortByChange(e.target.value as AdminUserSortBy)
              }
              style={selectStyle}>
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={toggleSortDirection}
              style={secondaryButtonStyle}
              aria-label={
                sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'
              }>
              {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
          <button
            onClick={() => setIsAdding(current => !current)}
            style={primaryButtonStyle}>
            {isAdding ? 'Cancel' : 'Add user'}
          </button>
        </div>

        {isAdding && (
          <form onSubmit={handleCreate} style={formStyle}>
            <input
              required
              maxLength={NAME_MAX}
              placeholder="Full name"
              value={newUser.fullName}
              onChange={e => setNewUser({...newUser, fullName: e.target.value})}
              style={inputStyle}
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={e => setNewUser({...newUser, email: e.target.value})}
              style={inputStyle}
            />
            <input
              required
              type="password"
              minLength={6}
              placeholder="Password"
              autoComplete="new-password"
              value={newUser.password}
              onChange={e => setNewUser({...newUser, password: e.target.value})}
              style={inputStyle}
            />
            <button
              type="submit"
              disabled={createMutation.isPending}
              style={primaryButtonStyle}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
            <p style={{color: '#999', margin: 0, flexBasis: '100%'}}>
              Creates a personal account. The user gets the usual confirmation
              email.
            </p>
          </form>
        )}

        {actionError && <p style={errorStyle}>{actionError}</p>}
        {notice && <p style={noticeStyle}>{notice}</p>}

        <p style={{color: '#999', margin: '0 0 16px'}}>
          {usersQuery.isPending
            ? 'Loading...'
            : keyword
              ? `${matchingUsers} matching ${matchingUsers === 1 ? 'user' : 'users'}`
              : ''}
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            opacity: usersQuery.isFetching && !usersQuery.isPending ? 0.6 : 1,
          }}>
          {users.map(user => {
            const isEditing = editingId === user.id;
            const isSelf = user.id === currentUserId;

            return (
              <div key={user.id} style={rowStyle}>
                {user.profilePhoto ? (
                  <img src={user.profilePhoto} alt="" style={avatarStyle} />
                ) : (
                  <div style={avatarStyle} />
                )}

                <div style={{flex: '1 1 180px', minWidth: 0}}>
                  {isEditing ? (
                    <input
                      autoFocus
                      maxLength={NAME_MAX}
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveEditing(user.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      style={{...inputStyle, width: '100%'}}
                    />
                  ) : (
                    <div style={nameStyle}>
                      {user.fullName || (
                        <span style={{color: '#777', fontWeight: 'normal'}}>
                          No name
                        </span>
                      )}
                      {isSelf ? ' (you)' : ''}
                    </div>
                  )}
                  <div style={metaStyle}>
                    {user.email}
                    {user.organizerName ? ` · ${user.organizerName}` : ''}
                    {user.createdAtUtc
                      ? ` · Joined ${formatCreatedAt(user.createdAtUtc)}`
                      : ''}
                  </div>
                </div>

                <span style={badgeStyle}>
                  {roleLabels[user.roleCode ?? 0] ?? 'No role'}
                </span>

                {isEditing ? (
                  <>
                    <button
                      onClick={() => saveEditing(user.id)}
                      disabled={updateMutation.isPending}
                      style={primaryButtonStyle}>
                      Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={secondaryButtonStyle}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEditing(user)}
                      style={secondaryButtonStyle}>
                      Rename
                    </button>
                    <button
                      onClick={() => setSettingsUserId(user.id)}
                      style={secondaryButtonStyle}>
                      Settings
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      disabled={isSelf || deleteMutation.isPending}
                      title={isSelf ? 'You cannot delete your own account' : ''}
                      style={isSelf ? disabledButtonStyle : dangerButtonStyle}>
                      Delete
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {usersQuery.isError && (
          <p style={messageStyle}>Could not load users. Try again.</p>
        )}

        {!usersQuery.isError && !usersQuery.isPending && users.length === 0 && (
          <p style={messageStyle}>No users match your search.</p>
        )}

        {settingsUserId && (
          <UserSettingsPanel
            userId={settingsUserId}
            onClose={() => setSettingsUserId(null)}
          />
        )}

        {maxPage > 1 && (
          <div style={paginationStyle}>
            <button
              onClick={() => setPageNumber(page => page - 1)}
              disabled={pageNumber <= 1}
              style={
                pageNumber <= 1 ? disabledButtonStyle : secondaryButtonStyle
              }>
              Previous
            </button>
            <span style={{color: '#999'}}>
              Page {pageNumber} of {maxPage}
            </span>
            <button
              onClick={() => setPageNumber(page => page + 1)}
              disabled={pageNumber >= maxPage}
              style={
                pageNumber >= maxPage
                  ? disabledButtonStyle
                  : secondaryButtonStyle
              }>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const screenStyle: React.CSSProperties = {
  backgroundColor: '#1a1a1a',
  minHeight: '100vh',
  padding: '40px 20px',
};

const statsRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  marginBottom: '30px',
};

const statCardStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '220px',
  padding: '24px',
  borderRadius: '12px',
  backgroundColor: '#242424',
  border: '1px solid #333',
  color: '#fff',
};

const highlightStatCardStyle: React.CSSProperties = {
  ...statCardStyle,
  backgroundColor: '#daff00',
  border: '1px solid #daff00',
  color: '#1a1a1a',
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '20px',
};

const selectStyle: React.CSSProperties = {
  height: '40px',
  padding: '0 12px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.85rem',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  marginBottom: '20px',
  borderRadius: '12px',
  backgroundColor: '#242424',
  border: '1px solid #333',
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '160px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '8px',
  border: '1px solid #333',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  fontSize: '0.9rem',
  boxSizing: 'border-box',
};

const buttonBaseStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  flexShrink: 0,
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: '#daff00',
  color: '#1a1a1a',
  border: '1px solid #daff00',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #333',
};

const dangerButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: 'transparent',
  color: '#ff4d4d',
  border: '1px solid #ff4d4d',
};

const disabledButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  color: '#555',
  cursor: 'not-allowed',
};

// On narrow screens the role and buttons wrap under the name.
const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 14px',
  borderRadius: '12px',
  backgroundColor: '#242424',
  border: '1px solid #333',
};

const avatarStyle: React.CSSProperties = {
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  objectFit: 'cover',
  backgroundColor: '#333',
  flexShrink: 0,
};

const nameStyle: React.CSSProperties = {
  color: '#fff',
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const metaStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.85rem',
  marginTop: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const badgeStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '2px 10px',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  backgroundColor: '#333',
  color: '#ddd',
  flexShrink: 0,
};

const errorStyle: React.CSSProperties = {
  color: '#ff4d4d',
  margin: '0 0 16px',
};

const noticeStyle: React.CSSProperties = {
  color: '#daff00',
  margin: '0 0 16px',
};

const messageStyle: React.CSSProperties = {
  color: '#666',
  textAlign: 'center',
  fontSize: '1.3rem',
  marginTop: '40px',
};

const paginationStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  marginTop: '30px',
};
